import { z } from "zod";
// Whitelist of fields exposable through the export builder. Adding a new
// export-eligible field means adding a value here so the API can validate
// preset/column bodies without trusting client-side strings.
//
// Only contact-owned fields are selectable. When a contact belongs to a
// household, the household's name and address override the contact's at
// resolution time (see fieldValue in apps/api/.../_helpers/csv.ts), so there
// are no separate `household.*` keys.
export const exportFieldKeySchema = z.enum([
    "contact.name",
    "contact.email",
    "contact.phone",
    "contact.notes",
    "contact.address.line1",
    "contact.address.line2",
    "contact.address.city",
    "contact.address.region",
    "contact.address.postalCode",
    "contact.address.country",
]);
/**
 * A single CSV output column. `fields.length === 1` is a plain column;
 * `> 1` combines those field values with `separator` (e.g. city + region +
 * postalCode → one address column). `visible: false` keeps a column in a saved
 * preset without emitting it, so it can be toggled back on without retyping.
 */
export const exportColumnSchema = z.object({
    fields: z.array(exportFieldKeySchema).min(1).max(10),
    header: z.string().min(1).max(200),
    separator: z.string().max(20).default(" "),
    visible: z.boolean(),
});
export const exportPresetInputSchema = z.object({
    name: z.string().min(1).max(200),
    columns: z.array(exportColumnSchema).min(1),
});
export const exportPresetSchema = exportPresetInputSchema.extend({
    id: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Schema for the inline (unsaved) column config carried on the export
 * download. The export page builds an ad-hoc list of columns and ships it as
 * a base64url-encoded JSON `columns` query param; the route decodes and
 * validates it with this schema.
 */
export const exportColumnsConfigSchema = z.array(exportColumnSchema).min(1).max(100);
/**
 * Default columns for a fresh export — a print/mailing-friendly layout of the
 * name and address. Address fields resolve to the household's canonical
 * mailing address for contacts that belong to one. Shared so the server
 * fallback and the web page start from one definition.
 */
export const DEFAULT_EXPORT_COLUMNS = [
    { fields: ["contact.name"], header: "Name", separator: " ", visible: true },
    { fields: ["contact.address.line1"], header: "Street", separator: " ", visible: true },
    { fields: ["contact.address.line2"], header: "Apt", separator: " ", visible: true },
    { fields: ["contact.address.city"], header: "City", separator: " ", visible: true },
    { fields: ["contact.address.region"], header: "State", separator: " ", visible: true },
    { fields: ["contact.address.postalCode"], header: "Zip", separator: " ", visible: true },
    { fields: ["contact.address.country"], header: "Country", separator: " ", visible: true },
];
//# sourceMappingURL=exportPreset.js.map